// apps/web/src/components/SubscriptionManagement.js
import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

/**
 * SubscriptionManagement - Admin interface for:
 * 1. Managing user subscription tiers
 * 2. Configuring which features belong to which tier
 * 3. Viewing subscription statistics
 */
export default function SubscriptionManagement() {
  const toast = useToast();

  const [activeTab, setActiveTab] = useState('users'); // 'users' | 'features' | 'chart'
  const [loading, setLoading] = useState(false);

  // User management state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedTier, setSelectedTier] = useState('free');

  // Feature management state
  const [features, setFeatures] = useState([]);
  const [editingFeatureId, setEditingFeatureId] = useState(null);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
      loadStats();
    } else if (activeTab === 'features') {
      loadFeatures();
    }
  }, [activeTab]);

  async function loadUsers() {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/subscriptions/users`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      toast.error('Failed to load users: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/subscriptions/stats`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }

  async function loadFeatures() {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/subscriptions/features`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      setFeatures(data.features || []);
    } catch (err) {
      toast.error('Failed to load features: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateUserTier(userId, newTier) {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/subscriptions/users/${userId}/tier`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ tier: newTier }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      toast.success('User tier updated successfully!');
      setEditingUserId(null);
      loadUsers();
      loadStats();
    } catch (err) {
      toast.error('Failed to update user tier: ' + err.message);
    }
  }

  async function updateFeatureTiers(featureId, tiers) {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/admin/subscriptions/features/${featureId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(tiers),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      toast.success('Feature tiers updated successfully!');
      loadFeatures();
    } catch (err) {
      toast.error('Failed to update feature tiers: ' + err.message);
    }
  }

  function handleFeatureTierToggle(feature, tier) {
    const tierKey = `${tier}_tier`;
    const newValue = !feature[tierKey];

    updateFeatureTiers(feature.id, {
      [tierKey]: newValue
    });
  }

  return (
    <div className="SubscriptionManagement">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, marginBottom: 8 }}>Subscription Management</h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: 14 }}>
          Manage user subscription tiers and configure feature access per tier
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 16,
          marginBottom: 24
        }}>
          <div style={{
            padding: 20,
            backgroundColor: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0'
          }}>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>Total Users</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1e293b' }}>{stats.total_users}</div>
          </div>
          <div style={{
            padding: 20,
            backgroundColor: '#dcfce7',
            borderRadius: 8,
            border: '1px solid #86efac'
          }}>
            <div style={{ fontSize: 13, color: '#166534', marginBottom: 4 }}>Free</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#166534' }}>{stats.by_tier?.free || 0}</div>
          </div>
          <div style={{
            padding: 20,
            backgroundColor: '#dbeafe',
            borderRadius: 8,
            border: '1px solid #93c5fd'
          }}>
            <div style={{ fontSize: 13, color: '#1e40af', marginBottom: 4 }}>Professional</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#1e40af' }}>{stats.by_tier?.professional || 0}</div>
          </div>
          <div style={{
            padding: 20,
            backgroundColor: '#f3e8ff',
            borderRadius: 8,
            border: '1px solid #c084fc'
          }}>
            <div style={{ fontSize: 13, color: '#7e22ce', marginBottom: 4 }}>Business</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: '#7e22ce' }}>{stats.by_tier?.business || 0}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: 8,
        marginBottom: 24,
        borderBottom: '2px solid #e2e8f0',
        paddingBottom: 0
      }}>
        <button
          onClick={() => setActiveTab('users')}
          style={{
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'users' ? '3px solid #3b82f6' : '3px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'users' ? '#3b82f6' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          👥 Users
        </button>
        <button
          onClick={() => setActiveTab('features')}
          style={{
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'features' ? '3px solid #3b82f6' : '3px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'features' ? '#3b82f6' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          ⚙️ Features Matrix
        </button>
        <button
          onClick={() => setActiveTab('chart')}
          style={{
            padding: '12px 20px',
            fontSize: 14,
            fontWeight: 600,
            border: 'none',
            borderBottom: activeTab === 'chart' ? '3px solid #3b82f6' : '3px solid transparent',
            backgroundColor: 'transparent',
            color: activeTab === 'chart' ? '#3b82f6' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s',
          }}
        >
          📊 Feature Comparison
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading...</div>
      ) : activeTab === 'users' ? (
        <UsersTab
          users={users}
          editingUserId={editingUserId}
          selectedTier={selectedTier}
          setEditingUserId={setEditingUserId}
          setSelectedTier={setSelectedTier}
          updateUserTier={updateUserTier}
        />
      ) : activeTab === 'features' ? (
        <FeaturesTab
          features={features}
          handleFeatureTierToggle={handleFeatureTierToggle}
        />
      ) : activeTab === 'chart' ? (
        <FeatureComparisonChart features={features} />
      ) : null}
    </div>
  );
}

/**
 * UsersTab - Manage user subscription tiers
 */
function UsersTab({ users, editingUserId, selectedTier, setEditingUserId, setSelectedTier, updateUserTier }) {
  function getTierColor(tier) {
    switch (tier) {
      case 'free': return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
      case 'professional': return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' };
      case 'business': return { bg: '#f3e8ff', text: '#7e22ce', border: '#c084fc' };
      default: return { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
    }
  }

  if (users.length === 0) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>No users found</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
        <thead>
          <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
            <th style={{ textAlign: 'left', padding: 12 }}>ID</th>
            <th style={{ textAlign: 'left', padding: 12 }}>Email</th>
            <th style={{ textAlign: 'left', padding: 12 }}>Role</th>
            <th style={{ textAlign: 'left', padding: 12 }}>Subscription Tier</th>
            <th style={{ textAlign: 'left', padding: 12 }}>Created</th>
            <th style={{ textAlign: 'center', padding: 12 }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const color = getTierColor(user.subscription_tier);
            const isEditing = editingUserId === user.id;

            return (
              <tr key={user.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                <td style={{ padding: 12 }}>{user.id}</td>
                <td style={{ padding: 12, fontWeight: 500 }}>{user.email}</td>
                <td style={{ padding: 12 }}>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600,
                    backgroundColor: user.role === 'admin' ? '#fef3c7' : '#f1f5f9',
                    color: user.role === 'admin' ? '#92400e' : '#475569'
                  }}>
                    {user.role || 'user'}
                  </span>
                </td>
                <td style={{ padding: 12 }}>
                  {isEditing ? (
                    <select
                      value={selectedTier}
                      onChange={(e) => setSelectedTier(e.target.value)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 6,
                        border: '1px solid #cbd5e1',
                        fontSize: 13,
                        fontWeight: 600
                      }}
                    >
                      <option value="free">Free</option>
                      <option value="professional">Professional</option>
                      <option value="business">Business</option>
                    </select>
                  ) : (
                    <span style={{
                      display: 'inline-block',
                      padding: '6px 12px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: color.bg,
                      color: color.text,
                      border: `1px solid ${color.border}`,
                      textTransform: 'capitalize'
                    }}>
                      {user.subscription_tier}
                    </span>
                  )}
                </td>
                <td style={{ padding: 12, color: '#64748b', fontSize: 13 }}>
                  {new Date(user.created_at).toLocaleDateString()}
                </td>
                <td style={{ padding: 12, textAlign: 'center' }}>
                  {isEditing ? (
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      <button
                        onClick={() => {
                          updateUserTier(user.id, selectedTier);
                        }}
                        style={{
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          border: '1px solid #10b981',
                          borderRadius: 6,
                          backgroundColor: '#10b981',
                          color: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        ✓ Save
                      </button>
                      <button
                        onClick={() => setEditingUserId(null)}
                        style={{
                          padding: '6px 12px',
                          fontSize: 12,
                          fontWeight: 600,
                          border: '1px solid #cbd5e1',
                          borderRadius: 6,
                          backgroundColor: 'white',
                          color: '#475569',
                          cursor: 'pointer'
                        }}
                      >
                        ✕ Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setEditingUserId(user.id);
                        setSelectedTier(user.subscription_tier);
                      }}
                      style={{
                        padding: '6px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                        border: '1px solid #3b82f6',
                        borderRadius: 6,
                        backgroundColor: '#eff6ff',
                        color: '#3b82f6',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * FeaturesTab - Configure which features belong to which tiers
 */
function FeaturesTab({ features, handleFeatureTierToggle }) {
  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {});

  function TierCheckbox({ feature, tier }) {
    const tierKey = `${tier}_tier`;
    const isChecked = feature[tierKey];

    return (
      <td style={{ textAlign: 'center', padding: 12 }}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => handleFeatureTierToggle(feature, tier)}
          style={{
            width: 18,
            height: 18,
            cursor: 'pointer',
            accentColor: '#3b82f6'
          }}
        />
      </td>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
        <p style={{ margin: 0, fontSize: 14, color: '#1e40af' }}>
          <strong>Configure Feature Access:</strong> Check the boxes to enable features for each subscription tier.
          Changes are saved automatically.
        </p>
      </div>

      {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => (
        <div key={category} style={{ marginBottom: 32 }}>
          <h3 style={{
            margin: 0,
            marginBottom: 12,
            padding: '8px 12px',
            backgroundColor: '#f8fafc',
            borderLeft: '4px solid #3b82f6',
            fontSize: 16,
            fontWeight: 600,
            textTransform: 'capitalize'
          }}>
            {category}
          </h3>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, marginBottom: 16 }}>
            <thead>
              <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ textAlign: 'left', padding: 12, width: '40%' }}>Feature</th>
                <th style={{ textAlign: 'left', padding: 12, width: '30%' }}>Description</th>
                <th style={{ textAlign: 'center', padding: 12, width: '10%' }}>
                  <div style={{ color: '#166534', fontWeight: 700 }}>Free</div>
                </th>
                <th style={{ textAlign: 'center', padding: 12, width: '10%' }}>
                  <div style={{ color: '#1e40af', fontWeight: 700 }}>Pro</div>
                </th>
                <th style={{ textAlign: 'center', padding: 12, width: '10%' }}>
                  <div style={{ color: '#7e22ce', fontWeight: 700 }}>Business</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {categoryFeatures.map((feature) => (
                <tr key={feature.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                  <td style={{ padding: 12, fontWeight: 500 }}>{feature.feature_name}</td>
                  <td style={{ padding: 12, color: '#64748b', fontSize: 13 }}>{feature.feature_description}</td>
                  <TierCheckbox feature={feature} tier="free" />
                  <TierCheckbox feature={feature} tier="professional" />
                  <TierCheckbox feature={feature} tier="business" />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

/**
 * FeatureComparisonChart - Visual comparison of features per tier
 */
function FeatureComparisonChart({ features }) {
  // Group features by category
  const groupedFeatures = features.reduce((acc, feature) => {
    if (!acc[feature.category]) {
      acc[feature.category] = [];
    }
    acc[feature.category].push(feature);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ marginBottom: 24, padding: 16, backgroundColor: '#fef3c7', border: '1px solid #fde047', borderRadius: 8 }}>
        <p style={{ margin: 0, fontSize: 14, color: '#92400e' }}>
          <strong>Feature Comparison Chart:</strong> This chart shows which features are included in each tier.
          Use this to understand the value proposition for each subscription level.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
        {/* Free Tier Column */}
        <div style={{ padding: 20, backgroundColor: '#dcfce7', borderRadius: 8, border: '2px solid #86efac' }}>
          <h3 style={{ margin: 0, marginBottom: 16, fontSize: 20, fontWeight: 700, color: '#166534' }}>
            Free
          </h3>
          {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => {
            const freeFeatures = categoryFeatures.filter(f => f.free_tier);
            if (freeFeatures.length === 0) return null;

            return (
              <div key={category} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', marginBottom: 8, textTransform: 'uppercase' }}>
                  {category}
                </div>
                {freeFeatures.map(feature => (
                  <div key={feature.id} style={{ fontSize: 13, color: '#166534', marginBottom: 4, paddingLeft: 12 }}>
                    ✓ {feature.feature_name}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Professional Tier Column */}
        <div style={{ padding: 20, backgroundColor: '#dbeafe', borderRadius: 8, border: '2px solid #93c5fd' }}>
          <h3 style={{ margin: 0, marginBottom: 16, fontSize: 20, fontWeight: 700, color: '#1e40af' }}>
            Professional
          </h3>
          {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => {
            const proFeatures = categoryFeatures.filter(f => f.professional_tier);
            if (proFeatures.length === 0) return null;

            return (
              <div key={category} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1e40af', marginBottom: 8, textTransform: 'uppercase' }}>
                  {category}
                </div>
                {proFeatures.map(feature => (
                  <div key={feature.id} style={{ fontSize: 13, color: '#1e40af', marginBottom: 4, paddingLeft: 12 }}>
                    ✓ {feature.feature_name}
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        {/* Business Tier Column */}
        <div style={{ padding: 20, backgroundColor: '#f3e8ff', borderRadius: 8, border: '2px solid #c084fc' }}>
          <h3 style={{ margin: 0, marginBottom: 16, fontSize: 20, fontWeight: 700, color: '#7e22ce' }}>
            Business
          </h3>
          {Object.entries(groupedFeatures).map(([category, categoryFeatures]) => {
            const businessFeatures = categoryFeatures.filter(f => f.business_tier);
            if (businessFeatures.length === 0) return null;

            return (
              <div key={category} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#7e22ce', marginBottom: 8, textTransform: 'uppercase' }}>
                  {category}
                </div>
                {businessFeatures.map(feature => (
                  <div key={feature.id} style={{ fontSize: 13, color: '#7e22ce', marginBottom: 4, paddingLeft: 12 }}>
                    ✓ {feature.feature_name}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
